import {
  Button,
  Checkbox,
  Icon,
  InputGroup,
  Menu,
  MenuItem,
  PopoverPosition,
  ResizeSensor,
} from '@blueprintjs/core';
import { getClassNamespace } from '@blueprintjs/core/lib/esm/common/classes';
import { IconNames } from '@blueprintjs/icons';
import { Popover2 } from '@blueprintjs/popover2';
import {
  Cell,
  Column,
  ColumnHeaderCell2,
  Region,
  SelectionModes,
  Table2,
  Utils,
} from '@blueprintjs/table';
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import * as React from 'react';
import { copyToClipboard, isEqual } from 'vet-bones/utils';
import { useDidValueChange } from 'vet-bones/utils/hooks';

import {
  TEST_IDS,
  getProjectDiagramModelTableColumnHeaderTestId,
  getProjectDiagramModelTableColumnHiddenTestId,
  getProjectDiagramModelTableColumnsFilterTestId,
} from 'src/constants/testIds';
import {
  BaseTableItem,
  TableField,
} from 'src/containers/ProjectDiagram/components/modelList/types';
import {
  INITIAL_COLUMN_WIDTH,
  adjustColumnWidths,
  fitColumnWidths,
  resetColumnWidths,
} from 'src/containers/ProjectDiagram/components/modelList/utils';
import { ModelListTableData } from 'src/store/ui/types';
import * as copy from 'src/templates/copy';

export type ProjectDiagramModelTableProps<TableItem extends BaseTableItem> = {
  fields: Array<TableField<TableItem>>;
  items: TableItem[];
  onUpdateTableData: (data: ModelListTableData<TableItem>) => void;
  tableData: ModelListTableData<TableItem> | null;
};

export const ProjectDiagramModelTable = <TableItem extends BaseTableItem>({
  fields: initialFields,
  items,
  onUpdateTableData,
  tableData: maybeTableData,
}: ProjectDiagramModelTableProps<TableItem>) => {
  const isMouseDown = React.useRef(false);

  const [columnFilter, setColumnFilter] = React.useState('');
  const [tableUpdateKey, forceTableUpdate] = React.useReducer((x) => x + 1, 0);

  const tableData = React.useMemo<ModelListTableData<TableItem>>(
    () =>
      maybeTableData || {
        fields: initialFields.map((field, fieldIdx) => ({
          ...field,
          fieldIdx,
        })),
        filter: '',
        sort: null,
        widths: undefined,
      },
    [initialFields, maybeTableData]
  );

  const { fields, filter: tableFilter, sort: tableSort, widths } = tableData;
  const handleTableDataUpdate = React.useCallback(
    (tableData: Partial<ModelListTableData<TableItem>>) => {
      onUpdateTableData({
        fields,
        filter: tableFilter,
        sort: tableSort,
        widths,
        ...tableData,
      });
      forceTableUpdate();
    },
    [fields, onUpdateTableData, tableFilter, tableSort, widths]
  );

  const columns = React.useMemo(
    () => fields.filter((field) => !field.hidden),
    [fields]
  );
  const columnWidths = React.useMemo(
    () => widths?.filter((_, fieldIdx) => !fields[fieldIdx]?.hidden),
    [fields, widths]
  );
  const columnIdxMap = React.useMemo(
    () =>
      widths
        ?.map((_, fieldIdx) => (fields[fieldIdx]?.hidden ? null : fieldIdx))
        .filter((width): width is NonNullable<typeof width> => width !== null),
    [fields, widths]
  );

  const itemIdxMap = React.useMemo(() => {
    const nextItemIdxMap = items.map((_, itemIdx) => itemIdx);
    if (!tableSort) {
      return nextItemIdxMap;
    }

    const maybeField = fields.find((field) => field.id === tableSort.field);
    if (!maybeField) {
      return nextItemIdxMap;
    }

    const { sorter } = maybeField;
    if (!sorter) {
      return nextItemIdxMap;
    }

    return nextItemIdxMap.sort((a, b) =>
      sorter(tableSort.direction)(items[a], items[b])
    );
  }, [fields, items, tableSort]);

  const itemFilter = React.useCallback(
    (itemIdx: number) =>
      !tableFilter.trim() ||
      items[itemIdx]?.name?.value
        .toLowerCase()
        .includes(tableFilter.trim().toLowerCase()),
    [items, tableFilter]
  );
  const rows = React.useMemo(
    () => items.filter((_, itemIdx) => itemFilter(itemIdx)),
    [itemFilter, items]
  );
  const rowIdxMap = React.useMemo(
    () => itemIdxMap.filter((itemIdx) => itemFilter(itemIdx)),
    [itemFilter, itemIdxMap]
  );

  const [containerWidth, setContainerWidth] = React.useState<number>(0);
  const didContainerWidthChange = useDidValueChange(containerWidth);

  const handleResize = React.useCallback((entries: ResizeObserverEntry[]) => {
    const width = entries[0]?.contentRect.width ?? 0;
    // extra width for scrollbar
    setContainerWidth(Math.max(width - 15, 0));
  }, []);

  const debouncedHandleResize = debounce(handleResize, 200, { leading: true });

  const didInitialFieldsChange = useDidValueChange(initialFields, isEqual);
  React.useEffect(() => {
    if (!didInitialFieldsChange) {
      return;
    }

    const initialFieldsById: Record<string, TableField<TableItem>> = {};
    const initialFieldsWithIdx = initialFields.map((field, fieldIdx) => {
      initialFieldsById[field.id] = field;
      return {
        ...field,
        fieldIdx,
      };
    });
    if (isEqual(initialFieldsWithIdx, fields)) {
      return;
    }

    const fieldIds = fields.map((field) => field.id);
    const addedFields = initialFieldsWithIdx.filter(
      (field) => !fieldIds.includes(field.id)
    );

    const removedFieldIdx = new Set<number>();
    const nextFields = fields
      .map((field, fieldIdx) => {
        const initialField = initialFieldsById[field.id];
        return initialField ? { ...initialField, fieldIdx } : field;
      })
      .filter((field, fieldIdx) => {
        if (!initialFieldsById[field.id]) {
          removedFieldIdx.add(fieldIdx);
          return false;
        }
        return true;
      })
      .concat(addedFields)
      .map((field, fieldIdx) => ({ ...field, fieldIdx }));

    let nextWidths: number[] | undefined;
    const addedWidthIdx = new Set<number>();
    if (widths && columnIdxMap) {
      nextWidths = widths.filter(
        (_, fieldIdx) => !removedFieldIdx.has(fieldIdx)
      );
      addedFields.forEach((_, fieldIdx) => {
        addedWidthIdx.add(widths.length + fieldIdx);
      });
      nextWidths = widths.concat(
        new Array<number>(addedFields.length).fill(INITIAL_COLUMN_WIDTH)
      );
    }

    const nextColumnIdxMap = nextWidths
      ?.map((_, fieldIdx) => (nextFields[fieldIdx]?.hidden ? null : fieldIdx))
      .filter((width): width is NonNullable<typeof width> => width !== null);

    if (containerWidth && nextWidths && nextColumnIdxMap) {
      nextWidths = fitColumnWidths({
        addedWidthIdx,
        containerWidth,
        columnIdxMap: nextColumnIdxMap,
        startWidthIdx: nextWidths.length - 1,
        widths: nextWidths,
      });
    }

    handleTableDataUpdate({
      fields: nextFields,
      widths: nextWidths,
    });
  }, [
    columnIdxMap,
    containerWidth,
    didInitialFieldsChange,
    fields,
    handleTableDataUpdate,
    initialFields,
    widths,
  ]);

  const handleColWidthChange = React.useCallback(
    (columnIdx, width) => {
      if (!widths || !columnIdxMap) {
        return;
      }

      handleTableDataUpdate({
        widths: adjustColumnWidths({
          columnIdx,
          columnIdxMap,
          containerWidth,
          width,
          widths,
        }),
      });
    },
    [columnIdxMap, containerWidth, handleTableDataUpdate, widths]
  );

  const handleToggleColumn = React.useCallback(
    (fieldIdx: number, nextHidden: boolean) => {
      if (fieldIdx === 0) {
        return;
      }

      const addedWidthIdx = new Set<number>();
      if (!nextHidden) {
        addedWidthIdx.add(fieldIdx);
      }

      const nextFields = fields.map((prevField) => ({
        ...prevField,
        hidden: prevField.fieldIdx === fieldIdx ? nextHidden : prevField.hidden,
      }));

      const nextColumnIdxMap = widths
        ?.map((_, fieldIdx) => (nextFields[fieldIdx]?.hidden ? null : fieldIdx))
        .filter((width): width is NonNullable<typeof width> => width !== null);

      handleTableDataUpdate({
        fields: nextFields,
        widths:
          widths && nextColumnIdxMap
            ? fitColumnWidths({
                addedWidthIdx,
                containerWidth,
                columnIdxMap: nextColumnIdxMap,
                startWidthIdx: fieldIdx,
                widths,
              })
            : widths,
      });
    },
    [containerWidth, fields, handleTableDataUpdate, widths]
  );

  const numColumns = fields.filter((field) => !field.hidden).length;
  React.useEffect(() => {
    if (columnWidths && columnWidths.length !== numColumns) {
      handleTableDataUpdate({
        widths:
          // reset if number of columns does not match
          resetColumnWidths({ containerWidth, fields, widths }),
      });
    } else if (containerWidth && didContainerWidthChange) {
      handleTableDataUpdate({
        widths:
          widths && columnIdxMap
            ? // shrink columns to fit within new container
              fitColumnWidths({
                containerWidth,
                columnIdxMap,
                startWidthIdx: widths.length - 1,
                widths,
              })
            : // reset if there are no widths set yet
              resetColumnWidths({ containerWidth, fields, widths }),
      });
    }
  }, [
    columnWidths,
    columnIdxMap,
    containerWidth,
    didContainerWidthChange,
    fields,
    handleTableDataUpdate,
    numColumns,
    widths,
  ]);

  const selectedRegions = React.useMemo(
    () =>
      columns.reduce<Region[]>((acc, _, columnIdx) => {
        rows.forEach((_, rowIdx) => {
          if (
            items[rowIdxMap[rowIdx] ?? rowIdx][
              fields[columnIdxMap?.[columnIdx] ?? columnIdx].value
            ]?.selected
          ) {
            acc.push({
              rows: [rowIdx, rowIdx],
              cols: [columnIdx, columnIdx],
            });
          }
        });
        return acc;
      }, []),
    [columnIdxMap, columns, fields, items, rowIdxMap, rows]
  );

  return (
    <div className="sd-project-diagram-model-table-container">
      <div className="sd-project-diagram-model-table-controls">
        <Popover2
          content={
            <Menu className="sd-project-diagram-model-table-menu">
              <InputGroup
                leftIcon={IconNames.SEARCH}
                onChange={(evt) => setColumnFilter(evt.target.value)}
                placeholder={
                  copy.components.projectDiagram.modelList.table
                    .columnPlaceholder
                }
                rightElement={
                  <Button
                    icon={IconNames.SMALL_CROSS}
                    minimal
                    onClick={() => setColumnFilter('')}
                    style={columnFilter ? {} : { visibility: 'hidden' }}
                  />
                }
                value={columnFilter}
              />
              <div className="sd-project-diagram-model-table-menu-items">
                {fields.map((field, fieldIdx) => {
                  if (!fieldIdx) {
                    return null;
                  }

                  if (
                    columnFilter.trim() &&
                    !field.label
                      .toLowerCase()
                      .includes(columnFilter.trim().toLowerCase())
                  ) {
                    return null;
                  }

                  return (
                    <div
                      key={field.id}
                      className="sd-project-diagram-model-table-menu-item"
                      data-testid={getProjectDiagramModelTableColumnsFilterTestId(
                        field.id,
                        field.fieldIdx
                      )}
                    >
                      <Checkbox
                        checked={!field.hidden}
                        data-testid={
                          TEST_IDS.projectDiagram.modelList.table.columnsFilter
                            .checkbox
                        }
                        onChange={() => {
                          handleToggleColumn(fieldIdx, !field.hidden);
                        }}
                      >
                        {field.leftIcon ? (
                          <Icon
                            className="sd-project-diagram-model-menu-item-icon"
                            icon={field.leftIcon}
                          />
                        ) : null}
                        <span>{field.label}</span>
                        {field.rightIcon ? (
                          <Icon
                            className="sd-project-diagram-model-menu-item-icon"
                            color={field.rightIconColor}
                            icon={field.rightIcon}
                          />
                        ) : null}
                      </Checkbox>
                    </div>
                  );
                })}
              </div>
            </Menu>
          }
          minimal
          popoverClassName="sd-popover-menu"
          position={PopoverPosition.BOTTOM_RIGHT}
        >
          <Button
            data-testid={
              TEST_IDS.projectDiagram.modelList.table.columnsFilter.button
            }
            outlined
            text={copy.components.projectDiagram.modelList.table.columns}
          />
        </Popover2>

        <InputGroup
          data-testid={TEST_IDS.projectDiagram.modelList.table.rowFilter.input}
          leftIcon={IconNames.SEARCH}
          onChange={(evt) =>
            handleTableDataUpdate({ filter: evt.target.value })
          }
          placeholder={
            copy.components.projectDiagram.modelList.table.tablePlaceholder
          }
          rightElement={
            <Button
              data-testid={
                TEST_IDS.projectDiagram.modelList.table.rowFilter.clear
              }
              icon={IconNames.SMALL_CROSS}
              minimal
              onClick={() => handleTableDataUpdate({ filter: '' })}
              style={tableFilter ? {} : { visibility: 'hidden' }}
            />
          }
          value={tableFilter}
        />
      </div>

      <ResizeSensor onResize={debouncedHandleResize}>
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
        <div
          className="sd-project-diagram-model-table"
          onKeyDownCapture={(evt) => {
            const selectedText = window.getSelection()?.toString();
            if (
              (evt.metaKey || evt.ctrlKey) &&
              evt.key === 'c' &&
              selectedText
            ) {
              // Blueprint Table2's handleCopy calls stopPropagation on
              // Cmd+C, preventing native copy. Intercept in the capture
              // phase and write to clipboard before Blueprint can block it.
              copyToClipboard(selectedText);
              evt.stopPropagation();
            }
          }}
          onMouseDown={() => {
            isMouseDown.current = true;
          }}
          onMouseLeave={() => {
            if (isMouseDown.current) {
              // clear the re-ordering handle when the cursor leaves the container
              forceTableUpdate();
            }
          }}
          onMouseUp={() => {
            isMouseDown.current = false;
          }}
        >
          <Table2
            key={tableUpdateKey}
            cellRendererDependencies={[
              columnIdxMap,
              fields,
              items,
              rowIdxMap,
              widths,
            ]}
            columnWidths={
              columnWidths?.length !== columns.length ? undefined : columnWidths
            }
            defaultRowHeight={30}
            enableColumnReordering
            enableRowHeader={false}
            numRows={rows.length}
            onColumnsReordered={(oldColIdx, newColIdx, length) => {
              if (
                oldColIdx === newColIdx ||
                oldColIdx === 0 ||
                newColIdx === 0
              ) {
                // do not allow moving the first column
                return;
              }

              const oldIdx = columnIdxMap?.[oldColIdx] ?? oldColIdx;
              const newIdx = columnIdxMap?.[newColIdx] ?? newColIdx;

              const nextFields = Utils.reorderArray(
                fields,
                oldIdx,
                newIdx,
                length
              );

              const nextWidths =
                widths && Utils.reorderArray(widths, oldIdx, newIdx, length);

              handleTableDataUpdate({
                fields:
                  nextFields?.map((field, fieldIdx) => ({
                    ...field,
                    fieldIdx,
                  })) ?? fields,
                widths: nextWidths ?? widths,
              });
            }}
            onColumnWidthChanged={handleColWidthChange}
            selectedRegions={selectedRegions}
            selectionModes={SelectionModes.NONE}
          >
            {columns.map((column) => {
              const fieldSorter = column.sorter;
              return (
                <Column
                  key={column.label}
                  cellRenderer={(rowIdx: number, columnIdx: number) => (
                    <Cell
                      className={classNames(
                        'sd-project-diagram-model-table-cell',
                        column.cellClassName
                      )}
                    >
                      {
                        items[rowIdxMap[rowIdx] ?? rowIdx][
                          fields[columnIdxMap?.[columnIdx] ?? columnIdx].value
                        ].label
                      }
                    </Cell>
                  )}
                  columnHeaderCellRenderer={() => (
                    <ColumnHeaderCell2
                      className={classNames(
                        'sd-project-diagram-model-table-cell',
                        column.headerClassName
                      )}
                      menuRenderer={() => (
                        <Menu>
                          {fieldSorter ? (
                            <>
                              <MenuItem
                                data-testid={
                                  TEST_IDS.projectDiagram.modelList.table
                                    .columnMenu.sortAsc
                                }
                                icon={column.sortIconAsc || IconNames.SORT_ASC}
                                onClick={() =>
                                  handleTableDataUpdate({
                                    sort: {
                                      field: column.id,
                                      direction: 'asc',
                                    },
                                  })
                                }
                                text={column.sortLabelAsc}
                              />
                              <MenuItem
                                data-testid={
                                  TEST_IDS.projectDiagram.modelList.table
                                    .columnMenu.sortDesc
                                }
                                icon={
                                  column.sortIconDesc || IconNames.SORT_DESC
                                }
                                onClick={() =>
                                  handleTableDataUpdate({
                                    sort: {
                                      field: column.id,
                                      direction: 'desc',
                                    },
                                  })
                                }
                                text={column.sortLabelDesc}
                              />
                            </>
                          ) : null}
                          {column.fieldIdx ? (
                            <MenuItem
                              data-testid={
                                TEST_IDS.projectDiagram.modelList.table
                                  .columnMenu.hide
                              }
                              icon={IconNames.REMOVE_COLUMN}
                              onClick={() => {
                                handleToggleColumn(column.fieldIdx, true);
                              }}
                              text={
                                copy.components.projectDiagram.modelList.table
                                  .hide
                              }
                            />
                          ) : null}
                        </Menu>
                      )}
                      nameRenderer={() => (
                        <div
                          className={`${getClassNamespace()}-table-truncated-text`}
                          data-testid={getProjectDiagramModelTableColumnHeaderTestId(
                            column.id,
                            column.fieldIdx
                          )}
                        >
                          {column.leftIcon ? (
                            <Icon
                              className="sd-project-diagram-model-table-header-icon"
                              icon={column.leftIcon}
                            />
                          ) : null}
                          <span>{column.label}</span>
                          {column.rightIcon ? (
                            <Icon
                              className="sd-project-diagram-model-table-header-icon"
                              color={column.rightIconColor}
                              data-testid={getProjectDiagramModelTableColumnHiddenTestId(
                                column.id,
                                column.fieldIdx
                              )}
                              icon={column.rightIcon}
                            />
                          ) : null}
                        </div>
                      )}
                    />
                  )}
                  name={column.label}
                />
              );
            })}
          </Table2>
        </div>
      </ResizeSensor>
    </div>
  );
};
